import React, { Component } from 'react'
import { Link, withRouter } from 'react-router-dom'
import { Query } from '@apollo/client/react/components'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Tag, Tooltip, Alert } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers } from '@fortawesome/free-solid-svg-icons'
import gql from 'graphql-tag'
import * as gqlBuilder from 'gql-query-builder'
import isEqual from 'lodash/isEqual'

import S from '../Styles'
import Table from './Table'
import Header from './Header'
import config from '../../config'
import Loading from '../LoadingPage'
import * as Utils from '../Utils'
import { withResponsive } from '../Responsive'
import {
	getPersistentSearchQuery,
	setPersistentSearchQuery
} from '../../utils/persistentSearchQueries'

class TableV2 extends Component {
	updateVisibility = () => {
		this.setState({ visible: !document.hidden })
	}

	state = {
		displayedColumnKeys: [],
		displayedColumns: [],
		columns: [],
		query: null,
		sort: null,
		sortDirection: 1,
		search: null,
		filters: [],
		visible: true
	}

	componentDidMount() {
		this.loadColumns(this.props)
		document.addEventListener('visibilitychange', this.updateVisibility)
	}

	componentWillUnmount() {
		document.removeEventListener('visibilitychange', this.updateVisibility)
	}

	componentWillReceiveProps(props) {
		// If we have a different table, then reload
		if (!isEqual(props.storageKey, this.props.storageKey)) {
			this.loadColumns(props)
		}
	}

	loadColumns = (props) => {
		// Load saved params
		let storedParams = {}
		if (props.storageKey) {
			const storedJson = localStorage.getItem(props.storageKey)
			if (storedJson) {
				storedParams = JSON.parse(storedJson)
			}

			// we get the persistent search query.
			// The searchQueryKey can be missing in the props, in this case
			// the default global persistent query will be used
			storedParams.search = getPersistentSearchQuery(props.searchQueryKey)
		}

		// Displayed columns
		const columns = this.addGenericColumns(props)
		let displayedColumnKeys = []
		if (storedParams.displayedColumnKeys) {
			displayedColumnKeys = storedParams.displayedColumnKeys
		} else {
			displayedColumnKeys = this.defaultColumns(props)
		}

		const displayedColumns = displayedColumnKeys.map((c) => ({
			...columns[c],
			key: c
		}))

		// those columns need to be fetched even if they are not displayed, so we can search on them
		const searchableColumns = this.getSearchableColumns(columns)

		// Apply
		const query =
			props.data ? null : (
				this.buildQuery(props, displayedColumns, searchableColumns)
			)
		this.setState({
			columns,
			displayedColumnKeys,
			displayedColumns,
			query,
			...storedParams,
			// a table without a storageKey has nothing stored, the defaultSort
			// props let it still open on a meaningful order
			sort: storedParams.sort ?? props.defaultSort ?? null,
			sortDirection:
				storedParams.sortDirection ?? props.defaultSortDirection ?? 1
		})
	}

	/**
	 * ACTIONS
	 */

	selectColumns = (selected) => {
		const displayedColumns = selected.map((c) => ({
			...this.state.columns[c],
			key: c
		}))

		const searchableColumns = this.getSearchableColumns(
			this.state.columns
		)

		const query =
			this.props.data ?
				null
			:	this.buildQuery(this.props, displayedColumns, searchableColumns)
		this.setState(
			{
				displayedColumnKeys: selected,
				displayedColumns,
				query
			},
			this.saveParams
		)
	}

	saveParams = () => {
		if (this.props.storageKey) {
			let s = {
				displayedColumnKeys: this.state.displayedColumnKeys,
				sort: this.state.sort,
				sortDirection: this.state.sortDirection,
				filters: this.state.filters
			}
			localStorage.setItem(this.props.storageKey, JSON.stringify(s))

			// searchQueryKey prop can be null, in this case the default query will be updated
			setPersistentSearchQuery(
				this.props.searchQueryKey,
				this.state.search
			)
		}
	}

	/**
	 * HELPERS
	 */

	defaultColumns = (props) => {
		if (props.mobileVersion && props.defaultMobileColumns) {
			return props.defaultMobileColumns
		}
		return props.defaultColumns
	}

	// Returns the searchable columns (with their key) so they can always be
	// fetched, even when not displayed, to allow searching into them
	getSearchableColumns = (columns) =>
		Object.entries(columns)
			.filter(([, c]) => c.searchable)
			.map(([key, c]) => ({ ...c, key }))

	buildQuery = (props, displayedColumns, searchableColumns) => {
		let qProps = ['_id']
		if (props.extraQueryProps) {
			qProps = [...qProps, ...props.extraQueryProps]
		}
		for (let c of displayedColumns) {
			if (c.queryProps) {
				qProps = [...qProps, ...c.queryProps]
			}
		}

		// we always fetch the searchable columns, to get the data
		// even if they are not displayed so we can search into it.
		// columns already displayed are skipped to avoid fetching them twice
		const displayedKeys = new Set(displayedColumns.map((c) => c.key))
		for (let c of searchableColumns ?? []) {
			if (c.queryProps && !displayedKeys.has(c.key)) {
				qProps = [...qProps, ...c.queryProps]
			}
		}

		const query = gqlBuilder.query(
			{
				operation: props.queryType,
				fields: qProps
			},
			null,
			{
				operationName: `${props.queryType}TableBuiltQuery`
			}
		)

		// eslint-disable-next-line
		return gql`
			${query.query}
		`
	}

	addGenericColumns = (props) => {
		const {
			link,
			path,
			intl,
			renderName,
			nameProp,
			nameQueryProps,
			teamProp,
			teamsProp,
			columns
		} = props
		let cols = { ...columns }
		if (nameProp) {
			cols['name'] = {
				queryProps: nameQueryProps || ['name'],
				name: intl.formatMessage({ id: 'name' }),
				accessor: nameProp,
				render: (record) => {
					if (renderName) {
						return renderName(record)
					}
					if (link) {
						return (
							<Link to={`${path}/${record._id}`}>
								{nameProp(record)}
							</Link>
						)
					}
					return nameProp(record)
				},
				noFilter: true
			}
		}

		if (teamProp) {
			cols['team'] = {
				// always fetch the data for the team as it will be searched into
				searchable: true,
				queryProps: [{ team: ['_id', 'n', 'path'] }],
				name: intl.formatMessage({ id: 'team' }),
				title: <FontAwesomeIcon icon={faUsers} />,
				accessor: (v) => {
					const c = teamProp(v)
					if (c) {
						return c.n
					}
					return ''
				},
				onFilter: (value, record) => {
					let rt = teamProp(record)
					if (rt && rt.path) {
						return rt.path.includes(value || '')
					}
					return false
				},
				render: (props) => {
					const t = teamProp(props)
					if (!t) {
						return ''
					}
					return (
						<Tag
							key={`${props._id}-${t.path}`}
							title={t.path}
							color="blue"
							style={{ cursor: 'pointer' }}
							onClick={(e) => {
								e.stopPropagation()
								this.props.history.push(`/teams/${t._id}`)
							}}
						>
							{t.n}
						</Tag>
					)
				},
				type: 'Team'
			}
		}

		if (teamsProp) {
			cols['team'] = {
				// always fetch the data for the team as it will be searched into
				searchable: true,
				queryProps: [{ teams: ['_id', 'n', 'path'] }],
				name: intl.formatMessage({ id: 'teams' }),
				title: <FontAwesomeIcon icon={faUsers} />,
				accessor: (v) => {
					const t = teamsProp(v)
					if (t) {
						return t.map((team) => team.n).join(', ')
					}
					return ''
				},
				onFilter: (value, record) => {
					let rt = teamsProp(record)
					if (rt && rt.length) {
						for (let t of rt) {
							if (t.path.includes(value || '')) {
								return true
							}
						}
					}
					return false
				},
				render: (props) => {
					const t = teamsProp(props)
					if (!t || !t.length) {
						return ''
					}
					if (t.length > 2) {
						return (
							<Tooltip
								title={
									<ul style={S.noBulletList}>
										{t.map((team) => (
											<li
												key={`${props._id}-${team.path}`}
											>
												{team.n}
											</li>
										))}
									</ul>
								}
							>
								<Tag color="blue">
									{t.length} <FormattedMessage id="teams" />
								</Tag>
							</Tooltip>
						)
					}
					return t.map((team) => (
						<Tag
							key={`${props._id}-${team.path}`}
							title={team.path}
							color="blue"
						>
							{team.n}
						</Tag>
					))
				},
				type: 'Team'
			}
		}

		return cols
	}

	render() {
		const {
			path,
			selectedId,
			onSelect,
			history,
			redirect,
			intl,
			title,
			actions,
			searchProps,
			nameProp,
			showSearch,
			emptyText,
			rowDisabled
		} = this.props
		const poll = !!title

		const result = (data) => (
			<React.Fragment>
				{(title || showSearch) && (
					<Header
						title={title}
						columns={this.state.columns}
						selectedColumns={this.state.displayedColumnKeys}
						selectedColumnsChanged={this.selectColumns}
						actions={actions}
						search={this.state.search}
						searchChanged={(v) =>
							this.setState({ search: v }, this.saveParams)
						}
						data={data}
						filters={this.state.filters}
						filtersChanged={(v) =>
							this.setState({ filters: v }, this.saveParams)
						}
					/>
				)}
				<Table
					rowKey={this.props.rowKey || '_id'}
					dataTest={this.props.dataTest}
					columns={this.state.displayedColumns}
					allColumns={this.state.columns}
					data={data}
					selected={[selectedId]}
					sort={this.state.sort}
					sortDirection={this.state.sortDirection}
					sortChanged={(sort, sortDirection) =>
						this.setState({ sort, sortDirection }, this.saveParams)
					}
					search={this.state.search}
					searchProps={searchProps}
					nameProp={nameProp}
					filters={this.state.filters}
					emptyText={intl.formatMessage({
						id: emptyText || 'noResults'
					})}
					rowDisabled={rowDisabled}
					onSelect={(r) => {
						if (onSelect) {
							onSelect(r)
						} else if (redirect) {
							history.push(`${path}/${r._id}`)
						}
					}}
				/>
			</React.Fragment>
		)

		if (this.props.data) {
			// data is supplied, no need for a query
			return result(this.props.data)
		}

		if (!this.state.query) {
			return <Loading />
		}

		return (
			<Query
				pollInterval={
					this.state.visible && poll ? config.longPollInterval : 0
				}
				query={this.state.query}
			>
				{({ loading, error, data }) => {
					if ((!data || !data[this.props.queryType]) && loading) {
						return (
							<React.Fragment>
								{title && <Header title={title} />}
								<Loading />
							</React.Fragment>
						)
					}
					if (error) {
						return (
							<React.Fragment>
								{title && <Header title={title} />}
								<Alert
									message={intl.formatMessage({
										id: 'error'
									})}
									description={Utils.displayGraphQLErrors(
										error
									)}
									type="error"
									showIcon
								/>
							</React.Fragment>
						)
					}

					return result(data[this.props.queryType])
				}}
			</Query>
		)
	}
}

export default withRouter(injectIntl(withResponsive(TableV2)))
